import type { PropsWithChildren } from 'react'
import { useIntl } from '@/intl'
import { ItemContextProvider } from '@/scopes/search/components/QuickSearchBarLegacy/Item/context'

import { QuickSearchDrawer } from '@/scopes/search/components/QuickSearchBarLegacy/QuickSearchContentLegacy'
import * as Collapsible from '@radix-ui/react-collapsible'

import {
  Button,
  horizontalGap,
  Icon,
  tk,
  Typography,
  verticalGap,
  withStyleModifiers,
} from '@sunwebgroup/ui-components'

import * as React from 'react'
import { css, styled } from 'styled-components'

const Wrapper = styled(Collapsible.Root)`
  padding: 0 ${tk('spacing_md')};
  width: 100%;
`

const Content = styled.div`
  align-items: center;
  background-color: ${tk('color_semantic_bg')};
  border-radius: ${tk('radius_rounded')};
  box-shadow: ${tk('shadow_md_bottom')};
  display: flex;
  flex-direction: row;
  height: var(--quicksearch-bar-height);
  justify-content: space-between;
  padding: ${tk('spacing_sm')} ${tk('spacing_sm')} ${tk('spacing_sm')} ${tk('spacing_lg')};
  position: relative;
  width: 100%;
`

const Backdrop = styled(Collapsible.Trigger)`
  cursor: default;
  position: fixed;
  background: ${tk('color_palette_transparent_black_25')};
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 1;
`

const InputItems = styled.div`
  margin-bottom: ${tk('spacing_md')};
  ${verticalGap('spacing_xs')}
`

const BarTrigger = styled.div`
  align-items: center;
  border-radius: ${tk('radius_rounded')};
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
`

const Box = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding: ${tk('spacing_md')} 0 ${tk('spacing_md')};
  ${verticalGap('spacing_2xs')}
`

const IconItems = styled.div`
  display: grid;
  grid-template-columns: repeat(4, auto);
  ${horizontalGap('spacing_xs')} @media ${tk('mq_tablet')} {
    ${horizontalGap('spacing_md')}
  }
`

const Actions = styled.div`
  padding: ${tk('spacing_sm')} 0;
`

const Action = styled(Button)`
  //TODO: Remove this when the legacy DS has been deprecated
  //There is a rule in the legacy DS that adds a margin-bottom to p elements
  margin: 0;
`

function QuickSearchBarMobile({ children, className }: PropsWithChildren<{ className?: string }>) {
  const intl = useIntl()

  return (
    <Wrapper className={className}>
      <Content>
        <Collapsible.Content>
          <Backdrop />
          <QuickSearchDrawer>
            <InputItems>
              <ItemContextProvider value={{ type: 'dialog' }}>{children}</ItemContextProvider>
            </InputItems>
            <Collapsible.Trigger asChild>
              <Button $fullSize>
                {intl.formatMessage({
                  id: 'search_apply_filters',
                })}
              </Button>
            </Collapsible.Trigger>
          </QuickSearchDrawer>
        </Collapsible.Content>
        <Collapsible.Trigger asChild>
          <BarTrigger>
            <Box>
              <Typography $variant="body-md-bold">{intl.formatMessage({ id: 'quicksearch_title' })}</Typography>
              <IconItems>
                <ItemContextProvider value={{ type: 'icon' }}>{children}</ItemContextProvider>
              </IconItems>
            </Box>
            <Actions>
              <Action as="p" $hierarchy="secondary" $withoutColor>
                <Icon glyph="IconPencil" />
              </Action>
            </Actions>
          </BarTrigger>
        </Collapsible.Trigger>
      </Content>
    </Wrapper>
  )
}

function isFullWidthMod() {
  return css`
  padding: 0;

  ${Content} {
    border-radius: 0;
    padding: ${tk('spacing_md')};
  }
`
}

export default withStyleModifiers(styled(QuickSearchBarMobile)``, {
  isFullWidthMod,
})
